// Estas regras existem somente na janela de impressão. A aula na tela
// conserva suas fontes, margens, imagens e acompanhamento de progresso.
export const COMPACT_PRINT_STYLES = `
  @page { size: A4 portrait; margin: 8mm; }
  @media print {
    html { font-size: 14px !important; }
    html, body {
      margin: 0 !important; padding: 0 !important;
      width: auto !important; min-height: 0 !important;
      background: white !important; color: #24323F;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 9.5pt !important; line-height: 1.2 !important;
    }
    [data-dm-print-layout] {
      padding-top: var(--print-padding-top) !important;
      padding-right: var(--print-padding-right) !important;
      padding-bottom: var(--print-padding-bottom) !important;
      padding-left: var(--print-padding-left) !important;
      margin-top: var(--print-margin-top) !important;
      margin-bottom: var(--print-margin-bottom) !important;
      min-height: 0 !important; overflow: visible !important;
      box-shadow: none !important; break-inside: auto;
    }
    [data-dm-print-width] {
      width: auto !important; max-width: none !important;
      margin-left: 0 !important; margin-right: 0 !important;
    }
    [data-dm-print-text] {
      font-size: 9.5pt !important; line-height: 1.2 !important;
    }
    h1, h2, h3, h4, h5, h6 {
      line-height: 1.2 !important; margin: 4px 0 2px !important;
      break-after: avoid;
    }
    h1 { font-size: 15pt !important; }
    h2 { font-size: 12pt !important; }
    h3, h4, h5, h6 { font-size: 10.5pt !important; }
    p, ul, ol { margin-top: 2px !important; margin-bottom: 2px !important; }
    [data-dm-print-empty-line] { display: none; }
    li { margin-top: 1px !important; margin-bottom: 1px !important; }
    ul, ol { padding-left: 16px !important; }
    p { orphans: 3; widows: 3; }
    table { max-width: 100% !important; border-collapse: collapse; }
    th, td { padding: 3px 5px !important; font-size: 9.5pt !important; line-height: 1.2 !important; }
    thead { display: table-header-group; }
    tr { break-inside: avoid; }
    img, svg, canvas { max-width: 100%; height: auto; }
    figure, img, svg { break-inside: avoid; }
    pre { white-space: pre-wrap; overflow-wrap: anywhere; }
  }
`;

// Limita apenas os recuos dos blocos de texto. Diagramas e fórmulas mantêm
// sua disposição; um material pode preservar outro bloco com o atributo
// data-print-preserve-layout. Nenhum texto é removido para reduzir páginas.
export function prepareCompactPrint(documentToPrint) {
  const view = documentToPrint.defaultView;
  const preserved = "svg, canvas, math, .katex, .MathJax, [data-print-preserve-layout]";
  // Duas quebras seguidas reservavam uma linha vazia além da separação.
  // Mantém a primeira quebra e não toca nas quebras internas de fórmulas.
  documentToPrint.querySelectorAll("br + br").forEach((element) => {
    if (!element.closest(preserved)) element.setAttribute("data-dm-print-empty-line", "");
  });
  const containers = documentToPrint.querySelectorAll(
    "div, section, article, main, header, footer, aside, blockquote, figure",
  );
  containers.forEach((element) => {
    if (element.closest(preserved)) return;
    const style = view.getComputedStyle(element);
    if (!["block", "flow-root", "flex"].includes(style.display)) return;
    element.setAttribute("data-dm-print-layout", "");
    ["top", "right", "bottom", "left"].forEach((side) => {
      const padding = parseFloat(style.getPropertyValue(`padding-${side}`)) || 0;
      element.style.setProperty(`--print-padding-${side}`, `${Math.min(padding, 3)}px`);
    });
    ["top", "bottom"].forEach((side) => {
      const margin = parseFloat(style.getPropertyValue(`margin-${side}`)) || 0;
      element.style.setProperty(`--print-margin-${side}`, `${Math.min(margin, 4)}px`);
    });
    // Blocos centrais com largura limitada são comuns nos resumos antigos.
    if (style.maxWidth !== "none" || element.parentElement === documentToPrint.body) {
      element.setAttribute("data-dm-print-width", "");
    }
    if (Array.from(element.childNodes).some((node) => node.nodeType === 3 && node.textContent.trim())) {
      element.setAttribute("data-dm-print-text", "");
    }
  });
  documentToPrint.querySelectorAll("p, li, td, th, dt, dd").forEach((element) => {
    if (!element.closest(preserved)) element.setAttribute("data-dm-print-text", "");
  });
}

async function printWhenReady(prepareLayout) {
  // Espera as fontes e imagens, sem prender a impressão em um recurso externo.
  const assets = Array.from(document.images).map((image) =>
    image.complete ? Promise.resolve() : new Promise((resolve) => {
      image.addEventListener("load", resolve, { once: true });
      image.addEventListener("error", resolve, { once: true });
    }),
  );
  if (document.fonts) assets.push(document.fonts.ready);
  let timeout;
  await Promise.race([
    Promise.allSettled(assets),
    new Promise((resolve) => { timeout = setTimeout(resolve, 2000); }),
  ]);
  clearTimeout(timeout);
  prepareLayout(document);
  window.addEventListener("afterprint", () => window.close(), { once: true });
  window.focus();
  window.print();
}

export function buildPrintDocument(htmlContent) {
  return `<!DOCTYPE html>
    <html lang="pt-BR"><head><meta charset="utf-8">
      <title>Imprimir Conteúdo</title>
      <style>${COMPACT_PRINT_STYLES}</style>
    </head><body>${htmlContent || ""}
      <script>
        const prepareLayout = ${prepareCompactPrint.toString()};
        const printContent = ${printWhenReady.toString()};
        window.addEventListener("load", () => printContent(prepareLayout), { once: true });
      </script>
    </body></html>`;
}
